import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getApiUrl } from "../utils/api";
import { Link } from "react-router-dom";
import { FaComments, FaLightbulb, FaPen, FaRedo, FaTrash } from "react-icons/fa";
import BadgeIcon from "./BadgeIcon";
import EmptyState from "./EmptyState";
import LikeHeart, { LIKE_BURST_MS } from "./LikeHeart";
import MentionText, { buildUsernameMapFromDiscussions } from "./MentionText";
import { UserContext } from "../context/UserContext";
import { useToast } from "../context/ToastContext";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import {
  getAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import "../pages/PoemDetail.css";

const ACCENT = "#f26522";
const REPLY_PREVIEW_COUNT = 5;

const FILTERS = [
  { id: "all", label: "All" },
  { id: "prompt", label: "Prompts" },
  { id: "post", label: "Posts" },
];

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

function userLiked(likes, userId) {
  if (!userId || !Array.isArray(likes)) return false;
  return likes.map(String).includes(String(userId));
}

function formatWhen(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function GroupDiscussions({
  groupId,
  groupName,
  isCreator,
  discussionCount = 0,
  highlightDiscussionId = null,
  onCountChange,
}) {
  const { user } = useContext(UserContext);
  const toast = useToast();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filter, setFilter] = useState("all");
  const [composerKind, setComposerKind] = useState("post");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedReplyThreads, setExpandedReplyThreads] = useState({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [likingKey, setLikingKey] = useState(null);
  const [likeBurstKey, setLikeBurstKey] = useState(null);
  const mentionDebounceRef = useRef(null);
  const replyInputRefs = useRef({});
  const highlightDoneRef = useRef(false);
  const discussionsObserverRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const [mention, setMention] = useState({
    open: false,
    discussionId: null,
    start: 0,
    end: 0,
    query: "",
    items: [],
    index: 0,
  });

  const usernameToId = useMemo(
    () => buildUsernameMapFromDiscussions(discussions),
    [discussions]
  );

  const emitDiscussionCount = (count) => {
    onCountChangeRef.current?.(count);
    window.dispatchEvent(
      new CustomEvent("group-discussions-changed", {
        detail: { groupId, discussionCount: count },
      })
    );
  };

  const loadDiscussions = useCallback(
    async (page = 1, append = false, kind = "all", { silent = false } = {}) => {
      try {
        if (page === 1) {
          if (!silent) setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const params = new URLSearchParams({
          page: String(page),
          limit: "10",
        });
        if (kind && kind !== "all") {
          params.set("kind", kind);
        }

        const res = await fetch(
          getApiUrl(`/api/groups/${groupId}/discussions?${params.toString()}`),
          { credentials: "include" }
        );
        if (res.status === 401) {
          toast.error("Please log in to view discussions.");
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const list = data.discussions || [];

        if (append) {
          setDiscussions((prev) => [...prev, ...list]);
        } else {
          setDiscussions(list);
        }

        setPagination(
          data.pagination || {
            currentPage: page,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false,
          }
        );

        if (page === 1) {
          emitDiscussionCount(data.discussionCount ?? list.length);
        }
      } catch {
        if (!append && !silent) toast.error("Failed to load discussions.");
        if (!append) setDiscussions([]);
      } finally {
        if (page === 1) {
          if (!silent) setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [groupId, toast]
  );

  const loadMoreDiscussions = useCallback(() => {
    if (loadingMore || loading || !pagination.hasNextPage) return;
    loadDiscussions(pagination.currentPage + 1, true, filter);
  }, [
    loadingMore,
    loading,
    pagination.hasNextPage,
    pagination.currentPage,
    loadDiscussions,
    filter,
  ]);

  useEffect(() => {
    loadDiscussions(1, false, filter);
  }, [groupId, filter, loadDiscussions]);

  useInfiniteScroll({
    enabled: !loading,
    hasNextPage: pagination.hasNextPage,
    isLoading: loading,
    isLoadingMore: loadingMore,
    onLoadMore: loadMoreDiscussions,
    sentinelRef: discussionsObserverRef,
  });

  useEffect(() => {
    if (!highlightDiscussionId || loading || loadingMore) return;
    if (highlightDoneRef.current) return;

    const match = discussions.find(
      (d) => String(d._id) === String(highlightDiscussionId)
    );

    if (!match) {
      if (filter !== "all") {
        setFilter("all");
        return;
      }
      if (pagination.hasNextPage && !loadingMore) {
        loadMoreDiscussions();
      }
      return;
    }

    highlightDoneRef.current = true;
    setExpandedId(match._id);
    setHighlightedId(match._id);

    const scrollTimer = setTimeout(() => {
      document
        .getElementById(`discussion-${match._id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);

    const highlightTimer = setTimeout(() => setHighlightedId(null), 3000);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(highlightTimer);
    };
  }, [highlightDiscussionId, loading, loadingMore, discussions, pagination.hasNextPage, loadMoreDiscussions, filter]);

  useEffect(() => {
    highlightDoneRef.current = false;
  }, [highlightDiscussionId]);

  const resetComposer = () => {
    setTitle("");
    setText("");
    setComposerKind("post");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please log in to post.");
      return;
    }
    if (!text.trim()) {
      toast.error("Write something first.");
      return;
    }
    if (composerKind === "prompt" && !title.trim()) {
      toast.error("Prompts need a title.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions`),
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kind: composerKind,
            title: title.trim(),
            text: text.trim(),
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to post");
      setDiscussions((prev) => [data, ...prev]);
      emitDiscussionCount(discussionCount + 1);
      resetComposer();
      toast.success(composerKind === "prompt" ? "Prompt posted" : "Post shared");
    } catch (err) {
      toast.error(err.message || "Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchMentionSuggestions = (query) => {
    if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
    mentionDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          getApiUrl(`/api/user/search?q=${encodeURIComponent(query)}`),
          { credentials: "include" }
        );
        const users = (await res.json()) || [];
        setMention((m) => ({ ...m, items: users.slice(0, 8), index: 0 }));
      } catch {
        setMention((m) => ({ ...m, items: [], index: 0 }));
      }
    }, 120);
  };

  const handleMentionChange = (discussionId, e) => {
    const input = e.target;
    const cursor = input.selectionStart || 0;
    const upToCursor = input.value.slice(0, cursor);
    const match = upToCursor.match(/(^|\s)@([a-zA-Z0-9_.-]{1,30})$/);
    if (match) {
      const query = match[2];
      const start = cursor - query.length - 1;
      setMention((m) => ({
        ...m,
        open: true,
        discussionId,
        start,
        end: cursor,
        query,
      }));
      if (query.length >= 1) fetchMentionSuggestions(query);
      else setMention((m) => ({ ...m, items: [], index: 0 }));
    } else {
      setMention((m) => ({ ...m, open: false, discussionId: null, items: [] }));
    }
  };

  const applyMention = (username, discussionId) => {
    const { start, end } = mention;
    const draft = replyDrafts[discussionId] || "";
    const insert = `@${username} `;
    const before = draft.slice(0, start);
    const after = draft.slice(end);
    const next = before + insert + after;
    setReplyDrafts((prev) => ({ ...prev, [discussionId]: next }));
    const input = replyInputRefs.current[discussionId];
    if (input) {
      input.focus();
      const caret = before.length + insert.length;
      input.setSelectionRange(caret, caret);
    }
    setMention({
      open: false,
      discussionId: null,
      start: 0,
      end: 0,
      query: "",
      items: [],
      index: 0,
    });
  };

  const handleMentionKeyDown = (e, discussionId) => {
    if (!mention.open || mention.discussionId !== discussionId) return;
    const hasItems = mention.items && mention.items.length > 0;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!hasItems) return;
      setMention((m) => ({ ...m, index: (m.index + 1) % m.items.length }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!hasItems) return;
      setMention((m) => ({
        ...m,
        index: (m.index - 1 + m.items.length) % m.items.length,
      }));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (mention.items[mention.index]) {
        e.preventDefault();
        applyMention(mention.items[mention.index].username, discussionId);
      } else {
        setMention((m) => ({ ...m, open: false }));
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention((m) => ({ ...m, open: false }));
    }
  };

  const handleReply = async (discussionId) => {
    const draft = (replyDrafts[discussionId] || "").trim();
    if (!draft) return;

    setReplyingId(discussionId);
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions/${discussionId}/replies`),
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: draft }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to reply");

      let newReplyCount = 0;
      setDiscussions((prev) =>
        prev.map((discussion) => {
          if (String(discussion._id) !== String(discussionId)) return discussion;
          const replies = [...(discussion.replies || []), data];
          newReplyCount = replies.length;
          return {
            ...discussion,
            replies,
            replyCount: replies.length,
          };
        })
      );
      setReplyDrafts((prev) => ({ ...prev, [discussionId]: "" }));
      setMention({
        open: false,
        discussionId: null,
        start: 0,
        end: 0,
        query: "",
        items: [],
        index: 0,
      });
      setExpandedId(discussionId);
      if (newReplyCount > REPLY_PREVIEW_COUNT) {
        setExpandedReplyThreads((prev) => ({
          ...prev,
          [String(discussionId)]: true,
        }));
      }
    } catch (err) {
      toast.error(err.message || "Failed to reply");
    } finally {
      setReplyingId(null);
    }
  };

  const handleDeleteDiscussion = async (discussionId) => {
    if (!window.confirm("Delete this discussion and all replies?")) return;
    setDeletingId(discussionId);
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions/${discussionId}`),
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete");
      }
      setDiscussions((prev) =>
        prev.filter((d) => String(d._id) !== String(discussionId))
      );
      emitDiscussionCount(Math.max(0, discussionCount - 1));
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteReply = async (discussionId, replyId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions/${discussionId}/replies/${replyId}`),
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete");
      }
      setDiscussions((prev) =>
        prev.map((discussion) => {
          if (String(discussion._id) !== String(discussionId)) return discussion;
          const replies = (discussion.replies || []).filter(
            (r) => String(r._id) !== String(replyId)
          );
          return { ...discussion, replies, replyCount: replies.length };
        })
      );
    } catch (err) {
      toast.error(err.message || "Failed to delete reply");
    }
  };

  const handleLikeDiscussion = async (discussionId) => {
    if (!user) {
      toast.error("Please log in to like posts.");
      return;
    }
    const key = `d-${discussionId}`;
    const discussion = discussions.find(
      (d) => String(d._id) === String(discussionId)
    );
    const willLike = !userLiked(discussion?.likes, user._id);
    setLikingKey(key);
    if (willLike) {
      setLikeBurstKey(key);
      setTimeout(() => {
        setLikeBurstKey((current) => (current === key ? null : current));
      }, LIKE_BURST_MS);
    }
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions/${discussionId}/like`),
        { method: "PUT", credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to like");
      setDiscussions((prev) =>
        prev.map((discussion) =>
          String(discussion._id) === String(discussionId)
            ? {
                ...discussion,
                likes: data.likes || [],
                likeCount: data.likeCount ?? data.likes?.length ?? 0,
              }
            : discussion
        )
      );
    } catch (err) {
      toast.error(err.message || "Failed to like");
      setLikeBurstKey((current) => (current === key ? null : current));
    } finally {
      setLikingKey(null);
    }
  };

  const handleLikeReply = async (discussionId, replyId) => {
    if (!user) {
      toast.error("Please log in to like replies.");
      return;
    }
    const key = `r-${discussionId}-${replyId}`;
    const discussion = discussions.find(
      (d) => String(d._id) === String(discussionId)
    );
    const reply = discussion?.replies?.find(
      (r) => String(r._id) === String(replyId)
    );
    const willLike = !userLiked(reply?.likes, user._id);
    setLikingKey(key);
    if (willLike) {
      setLikeBurstKey(key);
      setTimeout(() => {
        setLikeBurstKey((current) => (current === key ? null : current));
      }, LIKE_BURST_MS);
    }
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${groupId}/discussions/${discussionId}/replies/${replyId}/like`),
        { method: "PUT", credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to like");
      setDiscussions((prev) =>
        prev.map((discussion) => {
          if (String(discussion._id) !== String(discussionId)) return discussion;
          return {
            ...discussion,
            replies: (discussion.replies || []).map((reply) =>
              String(reply._id) === String(replyId)
                ? {
                    ...reply,
                    likes: data.likes || [],
                  }
                : reply
            ),
          };
        })
      );
    } catch (err) {
      toast.error(err.message || "Failed to like");
      setLikeBurstKey((current) => (current === key ? null : current));
    } finally {
      setLikingKey(null);
    }
  };

  const canDeleteDiscussion = (discussion) =>
    user &&
    (String(discussion.createdBy?._id || discussion.createdBy) === String(user._id) ||
      isCreator);

  const canDeleteReply = (reply) =>
    user &&
    (String(reply.user?._id || reply.user) === String(user._id) || isCreator);

  return (
    <div className="group-discussions">
      <form className="group-discussions__composer" onSubmit={handleCreate}>
        <div className="group-discussions__composer-head">
          <div className="group-discussions__kind-toggle" role="tablist" aria-label="Post type">
            <button
              type="button"
              role="tab"
              aria-selected={composerKind === "post"}
              className={`group-discussions__kind-btn${
                composerKind === "post" ? " group-discussions__kind-btn--active" : ""
              }`}
              onClick={() => setComposerKind("post")}
            >
              <FaPen aria-hidden="true" />
              Post
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={composerKind === "prompt"}
              className={`group-discussions__kind-btn${
                composerKind === "prompt" ? " group-discussions__kind-btn--active" : ""
              }`}
              onClick={() => setComposerKind("prompt")}
            >
              <FaLightbulb aria-hidden="true" />
              Prompt
            </button>
          </div>
          <p className="group-discussions__composer-hint">
            {composerKind === "prompt"
              ? "Share a writing prompt for the group."
              : "Start a conversation — thoughts, questions, anything."}
          </p>
        </div>

        {composerKind === "prompt" && (
          <input
            type="text"
            className="group-discussions__title-input"
            placeholder="Prompt title, e.g. Write about a door left open"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
          />
        )}

        <textarea
          className="group-discussions__textarea"
          placeholder={
            composerKind === "prompt"
              ? "Add details or inspiration for the prompt…"
              : `What's on your mind in ${groupName}?`
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={2000}
        />

        <div className="group-discussions__composer-actions">
          <button
            type="submit"
            className="group-page__btn group-page__btn--primary group-page__btn--sm"
            disabled={submitting || !text.trim()}
          >
            {submitting
              ? "Posting…"
              : composerKind === "prompt"
              ? "Post prompt"
              : "Post"}
          </button>
        </div>
      </form>

      <div className="group-discussions__filters" role="tablist" aria-label="Filter discussions">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={filter === item.id}
            className={`group-discussions__filter${
              filter === item.id ? " group-discussions__filter--active" : ""
            }`}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="group-discussions__loading">Loading discussions…</p>
      ) : discussions.length === 0 ? (
        <EmptyState
          icon="comments"
          title={filter === "all" ? "No discussions yet" : `No ${filter}s yet`}
          message={
            filter === "all"
              ? "Post a prompt or start a conversation with your group."
              : `Be the first to add a ${filter === "prompt" ? "writing prompt" : "post"}.`
          }
        />
      ) : (
        <ul className="group-discussions__list">
          {discussions.map((discussion) => {
            const isExpanded = expandedId === discussion._id;
            const author = discussion.createdBy;
            const replies = discussion.replies || [];
            const threadKey = String(discussion._id);
            const repliesExpanded = expandedReplyThreads[threadKey];
            const hasHiddenReplies =
              replies.length > REPLY_PREVIEW_COUNT && !repliesExpanded;
            const visibleReplies = hasHiddenReplies
              ? replies.slice(0, REPLY_PREVIEW_COUNT)
              : replies;
            return (
              <li
                key={discussion._id}
                id={`discussion-${discussion._id}`}
                className={`group-discussions__item${
                  highlightedId === discussion._id
                    ? " group-discussions__item--highlight"
                    : ""
                }`}
              >
                <article className="group-discussions__card">
                  <header className="group-discussions__card-head">
                    <div className="group-discussions__author">
                      <img
                        src={getAvatarUrl(author?.avatar)}
                        alt=""
                        className="group-discussions__avatar"
                        onError={avatarOnError}
                      />
                      <div>
                        {author?.username ? (
                          <Link
                            to={`/users/${author.username}`}
                            className="group-discussions__author-name"
                          >
                            {author.username}
                            <BadgeIcon type={author.badge} className="text-[#f26522]" />
                          </Link>
                        ) : (
                          <span className="group-discussions__author-name">Member</span>
                        )}
                        <time
                          dateTime={discussion.createdAt}
                          className="group-discussions__time"
                        >
                          {formatWhen(discussion.createdAt)}
                        </time>
                      </div>
                    </div>
                    <div className="group-discussions__card-actions">
                      {discussion.kind === "prompt" && (
                        <span className="group-discussions__badge group-discussions__badge--prompt">
                          <FaLightbulb aria-hidden="true" />
                          Prompt
                        </span>
                      )}
                      {canDeleteDiscussion(discussion) && (
                        <button
                          type="button"
                          className="group-discussions__delete"
                          onClick={() => handleDeleteDiscussion(discussion._id)}
                          disabled={deletingId === discussion._id}
                          title="Delete discussion"
                        >
                          <FaTrash aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </header>

                  {discussion.title && (
                    <h3 className="group-discussions__title">{discussion.title}</h3>
                  )}
                  <p className="group-discussions__body">{discussion.text}</p>

                  <div className="group-discussions__footer">
                    <button
                      type="button"
                      className="group-discussions__toggle-replies"
                      onClick={() =>
                        setExpandedId(isExpanded ? null : discussion._id)
                      }
                    >
                      <FaComments aria-hidden="true" />
                      {isExpanded
                        ? "Hide replies"
                        : (discussion.replyCount || 0) > 0
                        ? `Reply · ${discussion.replyCount} ${
                            discussion.replyCount === 1 ? "reply" : "replies"
                          }`
                        : "Reply"}
                    </button>
                    <button
                      type="button"
                      className={`group-discussions__like${
                        userLiked(discussion.likes, user?._id)
                          ? " group-discussions__like--active"
                          : ""
                      }`}
                      onClick={() => handleLikeDiscussion(discussion._id)}
                      disabled={likingKey === `d-${discussion._id}`}
                      aria-label={
                        userLiked(discussion.likes, user?._id)
                          ? "Unlike post"
                          : "Like post"
                      }
                    >
                      {(() => {
                        const liked = userLiked(discussion.likes, user?._id);
                        const animating =
                          likeBurstKey === `d-${discussion._id}`;
                        return (
                          <>
                            <LikeHeart
                              liked={liked || animating}
                              animating={animating}
                              size={14}
                              likedColor={ACCENT}
                            />
                            <span>
                              {discussion.likeCount ?? discussion.likes?.length ?? 0}
                            </span>
                          </>
                        );
                      })()}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="group-discussions__replies">
                      {visibleReplies.length > 0 && (
                        <ul className="group-discussions__reply-list">
                          {visibleReplies.map((reply) => (
                            <li key={reply._id} className="group-discussions__reply">
                              <img
                                src={getAvatarUrl(reply.user?.avatar)}
                                alt=""
                                className="group-discussions__reply-avatar"
                                onError={avatarOnError}
                              />
                              <div className="group-discussions__reply-body">
                                <div className="group-discussions__reply-meta">
                                  {reply.user?.username ? (
                                    <Link
                                      to={`/users/${reply.user.username}`}
                                      className="group-discussions__reply-author"
                                    >
                                      {reply.user.username}
                                    </Link>
                                  ) : (
                                    <span className="group-discussions__reply-author">
                                      Member
                                    </span>
                                  )}
                                  <time
                                    dateTime={reply.createdAt}
                                    className="group-discussions__reply-time"
                                  >
                                    {formatWhen(reply.createdAt)}
                                  </time>
                                  <button
                                    type="button"
                                    className={`group-discussions__like group-discussions__like--sm${
                                      userLiked(reply.likes, user?._id)
                                        ? " group-discussions__like--active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      handleLikeReply(discussion._id, reply._id)
                                    }
                                    disabled={
                                      likingKey === `r-${discussion._id}-${reply._id}`
                                    }
                                    aria-label={
                                      userLiked(reply.likes, user?._id)
                                        ? "Unlike reply"
                                        : "Like reply"
                                    }
                                  >
                                    {(() => {
                                      const liked = userLiked(reply.likes, user?._id);
                                      const key = `r-${discussion._id}-${reply._id}`;
                                      const animating = likeBurstKey === key;
                                      return (
                                        <>
                                          <LikeHeart
                                            liked={liked || animating}
                                            animating={animating}
                                            size={12}
                                            likedColor={ACCENT}
                                          />
                                          <span>{reply.likes?.length ?? 0}</span>
                                        </>
                                      );
                                    })()}
                                  </button>
                                  {canDeleteReply(reply) && (
                                    <button
                                      type="button"
                                      className="group-discussions__reply-delete"
                                      onClick={() =>
                                        handleDeleteReply(discussion._id, reply._id)
                                      }
                                      title="Delete reply"
                                    >
                                      <FaTrash aria-hidden="true" />
                                    </button>
                                  )}
                                </div>
                                <p>
                                  <MentionText
                                    text={reply.text}
                                    usernameToId={usernameToId}
                                  />
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {hasHiddenReplies && (
                        <button
                          type="button"
                          className="group-discussions__show-more-replies"
                          onClick={() =>
                            setExpandedReplyThreads((prev) => ({
                              ...prev,
                              [threadKey]: true,
                            }))
                          }
                        >
                          Show {replies.length - REPLY_PREVIEW_COUNT} more{" "}
                          {replies.length - REPLY_PREVIEW_COUNT === 1
                            ? "reply"
                            : "replies"}
                        </button>
                      )}

                      <div className="group-discussions__reply-form">
                        <div className="group-discussions__reply-input-wrap">
                          <textarea
                            ref={(el) => {
                              replyInputRefs.current[discussion._id] = el;
                            }}
                            className="group-discussions__reply-input"
                            placeholder="Write a reply… (use @ to mention users)"
                            value={replyDrafts[discussion._id] || ""}
                            onChange={(e) => {
                              setReplyDrafts((prev) => ({
                                ...prev,
                                [discussion._id]: e.target.value,
                              }));
                              handleMentionChange(discussion._id, e);
                            }}
                            onKeyDown={(e) =>
                              handleMentionKeyDown(e, discussion._id)
                            }
                            rows={2}
                            maxLength={2000}
                          />
                          {mention.open &&
                            mention.discussionId === discussion._id && (
                              <div className="mention-suggest group-discussions__mention-suggest">
                                <ul>
                                  {(mention.items || []).map((u, i) => (
                                    <li
                                      key={u._id || i}
                                      className={i === mention.index ? "active" : ""}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyMention(u.username, discussion._id);
                                      }}
                                    >
                                      <img
                                        src={getAvatarUrl(u.avatar)}
                                        alt=""
                                        className="w-6 h-6 rounded-full object-cover"
                                        onError={avatarOnError}
                                      />
                                      <span className="font-medium">{u.username}</span>
                                      {u.name && (
                                        <span className="text-[#777777] ml-1">
                                          ({u.name})
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                  {(!mention.items || mention.items.length === 0) && (
                                    <li className="text-sm text-[#777777] px-3 py-2">
                                      No matches
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                        <button
                          type="button"
                          className="group-page__btn group-page__btn--primary group-page__btn--sm group-discussions__reply-submit"
                          onClick={() => handleReply(discussion._id)}
                          disabled={
                            replyingId === discussion._id ||
                            !(replyDrafts[discussion._id] || "").trim()
                          }
                        >
                          {replyingId === discussion._id ? "Sending…" : "Reply"}
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              </li>
            );
          })}
          {loadingMore && (
            <li className="group-discussions__loading group-discussions__loading--more">
              <div className="group-discussions__spinner" />
              <span>Loading more discussions…</span>
            </li>
          )}
          {pagination.hasNextPage && !loadingMore && (
            <li ref={discussionsObserverRef} className="group-discussions__load-more-wrap">
              <button
                type="button"
                onClick={loadMoreDiscussions}
                className="group-discussions__load-more"
              >
                <FaRedo aria-hidden="true" />
                Load more
              </button>
            </li>
          )}
          {!pagination.hasNextPage && discussions.length > 0 && (
            <li className="group-discussions__feed-end">You&apos;ve reached the end</li>
          )}
        </ul>
      )}
    </div>
  );
}
